<template>
  <f7-page
    class="repair-parts-home"
    :page-content="false"
  >
    <template #fixed>
      <PCPartsNavbar @add-clicked="navigateToCreatePart" />
    </template>

    <PCPartsFAB />
    <PCPartsToolbar @tab-changed="changeTab" />

    <f7-page-content>
      <UCPreload
        :show="isLoadingWorker"
        :number="3"
      />

      <f7-tabs
        v-if="worker && !isLoadingWorker"
        animated
        :class="{ 'content-parts-tabs': workParts && workParts.length > 0 }"
      >
        <PCPartsTab
          v-for="tab in Tabs"
          :id="`tab-${tab}`"
          :key="tab"
          :tab-active="currentTab === tab"
        />
      </f7-tabs>
    </f7-page-content>
  </f7-page>
</template>

<script setup>
// @ts-check

import { onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { useRepairParts } from '@/composables/useRepairParts';
import { useI18n } from 'vue-i18n';

import { HiNotification } from '@/js/common/HINotificationSupport';
import { Tabs } from '@/js/enums/externalRepairParts';
import { callFetchWorkerByUsername } from '@/js/services/repairs/ApiRepairs';
import { f7 } from 'framework7-vue';
import { logError } from '@/js/utils/log';

import PCPartsNavbar from '@/components/repairs/repairParts/index/PCPartsNavbar.vue';
import PCPartsFAB from '@/components/repairs/repairParts/index/PCPartsFAB.vue';
import PCPartsToolbar from '@/components/repairs/repairParts/index/PCPartsToolbar.vue';
import UCPreload from '@/components/common/UCPreload.vue';
import PCPartsTab from '@/components/repairs/repairParts/index/PCPartsTab.vue';

const { t } = useI18n();

function useWorkerData() {
  const isLoadingWorker = ref(false);

  const { userLoginData, userEnviroment } = storeToRefs(useAppConfigStore());
  const { worker, workPositions } = storeToRefs(useRepairPartsStore());

  /**
   * Fetches worker data.
   * @returns {Promise<void>}
   */
  async function getWorker() {
    if (!userLoginData.value || !userEnviroment.value) {
      return;
    }

    try {
      isLoadingWorker.value = true;
      const { data } = await callFetchWorkerByUsername(
        userLoginData.value.UserName,
        userEnviroment.value.EntornoDeTrabajo.GrupoEmpresarial.Id,
      );

      if (Array.isArray(data) && data.length > 0) {
        worker.value = data[0] ?? null;
      }
    } catch (error) {
      logError(error);
    } finally {
      isLoadingWorker.value = false;
    }
  }

  /** Resets worker data on component destroy. */
  function resetWorkerData() {
    // Reset worker to not load work parts first when entering into "Repairs" form
    worker.value = null;

    // As discussed with Juan Miranda, it was decided to reset the work positions when
    // logging out or closing the "Repairs" form.
    workPositions.value = null;
  }

  /** A method that is called for the 'beforeunload' listener */
  function handleBeforeUnload() {
    // This unload event is to catch when user closes the "Repairs" form without going back

    if (worker.value || workPositions.value) {
      resetWorkerData();
    }
  }

  onMounted(async () => {
    window.addEventListener('unload', handleBeforeUnload);

    try {
      await getWorker();
    } finally {
      if (!worker.value) {
        setTimeout(() => {
          f7.views.main.router.back();
          HiNotification.Alert('No se han podido obtener los datos del trabajador', t('common.error'));
        }, 500);
      }
    }
  });

  onBeforeUnmount(() => {
    resetWorkerData();
    window.removeEventListener('unload', handleBeforeUnload);
  });

  return {
    isLoadingWorker,
    worker,
  };
}

function useWorkParts() {
  const { workParts, currentTab, loadWorkParts } = useRepairParts();

  /**
   * Event that is called when user changes tab.
   * @param {number} newTab - The tab index.
   * @returns {void}
   */
  function changeTab(newTab) {
    f7.tab.show(`#tab-${newTab}`, true);
    loadWorkParts();
  }

  return {
    workParts,
    currentTab,
    changeTab,
  };
}

/**
 * Navigates to the create part view.
 * @returns {void}
 */
function navigateToCreatePart() {
  f7.views.main.router.navigate('/repairs/repairParts/createPart');
}

const { isLoadingWorker, worker } = useWorkerData();
const { workParts, currentTab, changeTab } = useWorkParts();
</script>

<style scoped lang="scss">
.repair-parts-home {
  .page-content {
    --f7-page-toolbar-top-offset: 76px;

    padding-bottom: calc(16px + var(--information-bar-height));
  }

  .tabs-animated-wrap {
    overflow-x: hidden;
    overflow-y: auto;
  }
}
</style>
